import React, { useMemo, useState } from 'react';

import {
  Product,
  ProductTypeEnum,
  useFeatureFlags,
} from '@portals/api/partners';
import { ModalProps, WizardProvider } from '@portals/framework';

import { calcPhysicalIntegrationType } from './physical-product/physical-product-utils';
import { STEPS_MAP, STEPS_MAP_WITH_PPP } from './steps-map';
import { ProductWizardContext, StepId } from './types';
import { WizardContent } from './WizardContent';

const initialErrors: ProductWizardContext['errors'] = {
  [StepId.Integration]: '',
  [StepId.GeneralInformation]: {
    name: '',
    description: '',
  },
  [StepId.ProductDetails]: {
    licenseData: '',
  },
  [StepId.PricingModel]: {
    general: '',
    usageBasedDisplayName: '',
    usageBasedTelemetryKey: '',
    usageBasedUnitName: '',
  },
  [StepId.TaxDetails]: '',
};

function composeInitialProduct(
  product: ProductWizardModalData['product']
): ProductWizardContext['product'] {
  return {
    name: product?.name,
    category: product?.category,
    subtitle: product?.subtitle,
    description: product?.description,
    image_url: product?.image_url,
    license_data: product?.license_data,
    tax_group_id: product?.tax_group_id,
    device_model_id: product?.device_model?.id,
    product_type: product?.product_type,
    post_purchase_parameters: product?.post_purchase_parameters,
    pricing_model: product?.pricing_model,
    sku: product?.sku,
    usage_based_display_name: product?.usage_based_display_name,
    usage_based_telemetry_key: product?.usage_based_telemetry_key,
    usage_based_unit_name: product?.usage_based_unit_name,
  };
}

type ProductWithoutId = Omit<Product, 'id'>;
export type ProductWizardModalData =
  | {
      intent: 'create';
      product: undefined;
    }
  | {
      intent: 'duplicate';
      product: ProductWithoutId;
    }
  | {
      intent: 'edit';
      product: Product;
    };

export interface ProductWizardModalProps
  extends ModalProps<ProductWizardModalData> {}

export function ProductWizardModal({
  closeMe,
  data: { intent, product },
}: ProductWizardModalProps) {
  const isEdit = intent === 'edit';

  const featureFlags = useFeatureFlags();
  const [contextData, setContextData] = useState<ProductWizardContext>({
    intent,

    product: composeInitialProduct(product),

    physicalIntegrationType: calcPhysicalIntegrationType(
      product?.product_type,
      product?.device_model?.id
    ),

    errors: initialErrors,
  });

  const steps = useMemo(() => {
    if (!featureFlags?.ppp) return STEPS_MAP;

    if (product && product.product_type !== ProductTypeEnum.Physical) {
      return STEPS_MAP;
    }

    return STEPS_MAP_WITH_PPP;
  }, [featureFlags?.ppp, product]);

  return (
    <WizardProvider<ProductWizardContext, StepId>
      steps={steps}
      initialStep={product ? StepId.Integration : StepId.ProductType}
      contextData={contextData}
      setContextData={setContextData}
    >
      <WizardContent
        productId={product && 'id' in product ? product.id : undefined}
        isEdit={isEdit}
        onClose={closeMe}
      />
    </WizardProvider>
  );
}
